import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MantineProvider, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { MsalProvider } from '@azure/msal-react';
import theme from './themes/theme';
import PlatformSelector from './components/PlatformSelector';
import ScrollToTop from './components/ScrollToTop';
import { PlatformProvider } from './shared/contexts/PlatformContext';

// Import platform apps
import AwsApp from './platforms/aws/AwsApp';
import CiscoApp from './platforms/cisco/CiscoApp';
import Five9App from './platforms/five9/Five9App';
import SaToolsHub from './platforms/satools/SaToolsHub';
import CcUtilsHub from './platforms/ccutils/CcUtilsHub';
import SitApp from './platforms/sit/SitApp';
import AgentStudioApp from './platforms/agentstudio/AgentStudioApp';
import IvrApp from './platforms/ivr/IvrApp';

// Import Lucid Charts callback component for OAuth authentication
import LucidAuthCallback from './platforms/aws/components/LucidAuthCallback';

// Import authentication components
import { AuthProvider } from './shared/contexts/AuthContext';
import AuthenticationGuard from './components/AuthenticationGuard';
import UserProfile from './components/UserProfile';
import msalInstance from './shared/config/msalInstance';

// Mantine styles
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import './App.css';

// MSAL instance is imported as singleton from msalInstance.js

function App() {
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // Available platforms configuration
  const platforms = {
    aws: {
      name: 'AWS',
      description: 'Comprehensive AWS Connect resource analysis, contact flow visualization, and export capabilities',
      color: 'orange',
      icon: '☁️',
      component: AwsApp
    },
    cisco: {
      name: 'Cisco WxCC',
      description: 'Detailed Design Document processing and WebEx Contact Center configuration automation',
      color: 'blue',
      icon: '🌐',
      component: CiscoApp
    },
    five9: {
      name: 'Five9',
      description: 'Five9 contact center configuration management and user administration tools',
      color: 'green',
      icon: '📞',
      component: Five9App
    },
    satools: {
      name: 'SA Tools',
      description: 'Interaction Process Automation (IPA) assessment and report generation tools',
      color: 'purple',
      icon: '🛠️',
      component: SaToolsHub
    },
    ccutils: {
      name: 'Contact Center Utilities',
      description: 'General-purpose contact center utilities including audio file generation and more',
      color: 'teal',
      icon: '🎧',
      component: CcUtilsHub
    },
    sit: {
      name: 'Service IVR Testing',
      description: 'Automated outbound test calls into CCaaS IVRs, LLM-evaluated against a structured test plan with CCaaS log correlation',
      color: 'violet',
      icon: '🤖',
      component: SitApp
    },
    agentstudio: {
      name: 'AI Agent Studio',
      description: 'Generate ready-to-import WxCC AI Agent JSON from a structured spec. Standards-driven prompting (data-collection patterns, confirmation gates, escalation paths) baked in.',
      color: 'cyan',
      icon: '✨',
      component: AgentStudioApp
    },
    ivr: {
      name: 'Conv. AI Use Case Library',
      description: 'Conversational IVR use cases extracted from SharePoint project documents',
      color: 'indigo',
      icon: '🧠',
      component: IvrApp
    }
  };

  const handlePlatformSelect = (platformKey) => {
    // Reset the URL so the platform always mounts at its landing/hub. Each
    // platform renders inside its own <Router> and reads window.location on
    // mount; without this, re-entering a platform would restore the stale URL
    // (e.g. /export) and skip the landing page. App lives outside the Router,
    // so window.history is the tool here — the freshly-mounted Router picks it
    // up on the next render.
    window.history.replaceState(null, '', '/');
    setSelectedPlatform(platformKey);
  };

  const handleBackToPlatformSelector = () => {
    // Clear the in-platform URL so the selector (and the next platform entry)
    // start clean. See handlePlatformSelect for why window.history is used.
    window.history.replaceState(null, '', '/');
    setSelectedPlatform(null);
  };

  // If no platform selected, show platform selector with authentication
  if (!selectedPlatform) {
    return (
      <MsalProvider instance={msalInstance}>
        <MantineProvider theme={theme}>
          <Notifications />
          <AuthProvider>
            <PlatformProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  {/* Lucid Charts OAuth callback route - requires authentication */}
                  <Route path="/auth/lucid/callback" element={
                    <AuthenticationGuard>
                      <LucidAuthCallback />
                    </AuthenticationGuard>
                  } />
                  {/* Default route shows platform selector - requires authentication */}
                  <Route path="/*" element={
                    <AuthenticationGuard>
                      <Box style={{ position: 'relative', minHeight: '100vh' }}>
                        {/* User Profile in top-right corner */}
                        <Box style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                          <UserProfile />
                        </Box>
                        <PlatformSelector
                          platforms={platforms}
                          onSelect={handlePlatformSelect}
                        />
                      </Box>
                    </AuthenticationGuard>
                  } />
                </Routes>
              </Router>
            </PlatformProvider>
          </AuthProvider>
        </MantineProvider>
      </MsalProvider>
    );
  }

  // Render selected platform app (placeholder for now)
  const selectedPlatformData = platforms[selectedPlatform];

  if (!selectedPlatformData) {
    return (
      <MsalProvider instance={msalInstance}>
        <MantineProvider theme={theme}>
          <Notifications />
          <AuthProvider>
            <AuthenticationGuard>
              <Box style={{ position: 'relative', minHeight: '100vh' }}>
                {/* User Profile in top-right corner */}
                <Box style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                  <UserProfile />
                </Box>
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <h2>Platform not found</h2>
                  <button onClick={handleBackToPlatformSelector}>
                    Back to Platform Selector
                  </button>
                </div>
              </Box>
            </AuthenticationGuard>
          </AuthProvider>
        </MantineProvider>
      </MsalProvider>
    );
  }

  // Render selected platform app
  const SelectedPlatformComponent = selectedPlatformData.component;

  if (SelectedPlatformComponent) {
    // Render the actual platform component with authentication
    return (
      <MsalProvider instance={msalInstance}>
        <MantineProvider theme={theme}>
          <Notifications />
          <AuthProvider>
            <PlatformProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  {/* Lucid Charts OAuth callback route - requires authentication */}
                  <Route path="/auth/lucid/callback" element={
                    <AuthenticationGuard>
                      <LucidAuthCallback />
                    </AuthenticationGuard>
                  } />
                  {/* All other routes go to selected platform - requires authentication */}
                  <Route
                    path="/*"
                    element={
                      <AuthenticationGuard>
                        <Box style={{ position: 'relative', minHeight: '100vh' }}>
                          {/* User Profile in top-right corner */}
                          <Box style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                            <UserProfile />
                          </Box>
                          <SelectedPlatformComponent
                            onBackToPlatformSelector={handleBackToPlatformSelector}
                            platformInfo={selectedPlatformData}
                          />
                        </Box>
                      </AuthenticationGuard>
                    }
                  />
                </Routes>
              </Router>
            </PlatformProvider>
          </AuthProvider>
        </MantineProvider>
      </MsalProvider>
    );
  }

  // Fallback for platforms without components yet
  return (
    <MsalProvider instance={msalInstance}>
      <MantineProvider theme={theme}>
        <Notifications />
        <AuthProvider>
          <PlatformProvider>
            <Router>
              <ScrollToTop />
              <Routes>
                {/* All other routes require authentication */}
                <Route path="/*" element={
                  <AuthenticationGuard>
                    <Box style={{ position: 'relative', minHeight: '100vh' }}>
                      {/* User Profile in top-right corner */}
                      <Box style={{ position: 'absolute', top: 20, right: 20, zIndex: 1000 }}>
                        <UserProfile />
                      </Box>
                      <div style={{ padding: '40px', textAlign: 'center' }}>
                        <h1>{selectedPlatformData.name} Platform</h1>
                        <p>{selectedPlatformData.description}</p>
                        <p style={{ color: '#666', marginTop: '20px' }}>
                          Platform implementation coming soon...
                        </p>
                        <button
                          onClick={handleBackToPlatformSelector}
                          style={{
                            marginTop: '20px',
                            padding: '12px 24px',
                            backgroundColor: '#228be6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          ← Back to Platform Selector
                        </button>
                      </div>
                    </Box>
                  </AuthenticationGuard>
                } />
              </Routes>
            </Router>
          </PlatformProvider>
        </AuthProvider>
      </MantineProvider>
    </MsalProvider>
  );
}

export default App;